// ═══════════════════════════════════════════════════════
// TOAST — Sistema global de notificações toast
// Substitui alert() por notificações visuais elegantes
// Sprint 10: Dark mode aware + role="alert" for a11y
// ═══════════════════════════════════════════════════════
import { useState, useCallback, createContext, useContext, type ReactNode } from "react";
import type { ThemeObject } from "../shared/theme.js";

type ToastType = "success" | "error" | "warning" | "info";
type AddToastFn = (message: string, type?: ToastType, duration?: number) => void;

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  exiting: boolean;
}

interface ToastProviderProps {
  children: ReactNode;
  T?: ThemeObject;
}

const ToastContext = createContext<AddToastFn | null>(null);

const TOAST_TYPES: Record<ToastType, { bg: string; darkBg: string; icon: string; label: string }> =
  {
    success: { bg: "#16A34A", darkBg: "#166534", icon: "✅", label: "Sucesso" },
    error: { bg: "#DC2626", darkBg: "#991B1B", icon: "❌", label: "Erro" },
    warning: { bg: "#D97706", darkBg: "#92400E", icon: "⚠️", label: "Aviso" },
    info: { bg: "#2563EB", darkBg: "#1E40AF", icon: "ℹ️", label: "Info" },
  };

export function ToastProvider({ children, T }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast: AddToastFn = useCallback((message, type = "info", duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type, exiting: false }]);

    // Start exit animation after duration
    setTimeout(() => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
      // Remove from DOM after animation
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 300);
    }, duration);
  }, []);

  // Detect dark mode from theme prop or fallback
  const isDark = T?.bg === "#0F172A" || T?.card === "#1E293B";

  return (
    <ToastContext.Provider value={addToast}>
      {children}
      <div
        style={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          pointerEvents: "none",
        }}
        aria-live="polite"
      >
        {toasts.map((t) => {
          const cfg = TOAST_TYPES[t.type] || TOAST_TYPES.info;
          const bg = isDark ? cfg.darkBg : cfg.bg;
          return (
            <div
              key={t.id}
              role="alert"
              aria-label={cfg.label}
              style={{
                background: bg,
                color: "#fff",
                borderRadius: 12,
                padding: "12px 20px",
                fontSize: 13,
                fontWeight: 600,
                boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.5)" : "0 8px 24px rgba(0,0,0,0.25)",
                display: "flex",
                alignItems: "center",
                gap: 8,
                pointerEvents: "auto",
                maxWidth: 360,
                opacity: t.exiting ? 0 : 1,
                transform: t.exiting ? "translateX(100%)" : "translateX(0)",
                transition: "all 0.3s ease",
                animation: "lpa-toast-in 0.3s ease",
                border: isDark ? "1px solid rgba(255,255,255,0.1)" : "none",
              }}
            >
              <span style={{ fontSize: 16 }}>{cfg.icon}</span>
              {typeof t.message === "string" ? t.message : String(t.message ?? "")}
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Hook para disparar toasts de qualquer componente.
 * Uso: const toast = useToast(); toast("Salvo!", "success");
 */
export function useToast(): AddToastFn {
  const ctx = useContext(ToastContext);
  if (!ctx) return (() => {}) as AddToastFn;
  return ctx;
}

// ── CSS injection for toast animation ──
if (typeof document !== "undefined" && !document.getElementById("lpa-toast-css")) {
  const s = document.createElement("style");
  s.id = "lpa-toast-css";
  s.textContent = `
    @keyframes lpa-toast-in {
      from { opacity: 0; transform: translateX(100%); }
      to   { opacity: 1; transform: translateX(0); }
    }
  `;
  document.head.appendChild(s);
}
